import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { Eye, X, FileText, ClipboardCheck } from "lucide-react";

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
    "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];
const SECTIONS = ["A", "B", "C"];

export default function Calificaciones() {
    const [exams, setExams] = useState([]);
    const [results, setResults] = useState([]);
    const [filterSubject, setFilterSubject] = useState("");
    const [filterBaseGrade, setFilterBaseGrade] = useState("");
    const [filterSection, setFilterSection] = useState("");
    const [sortDir, setSortDir] = useState("desc");
    const [viewing, setViewing] = useState(null);

    async function loadAll() {
        const r = await api.get("/exams");
        setExams(r.data || []);
        const all = [];
        for (const e of r.data || []) {
            try {
                const rr = await api.get(`/exams/${e.id}/results`);
                all.push(...(rr.data || []).map((res) => ({
                    ...res,
                    subject: e.subject,
                    grade: e.grade,
                    kind: "examen",
                })));
            } catch (_) {}
        }
        try {
            const ar = await api.get("/assignments/results");
            all.push(...(ar.data || []));
        } catch (_) {}
        setResults(all);
    }

    useEffect(() => { loadAll(); }, []);

    let filtered = results;
    if (filterSubject) filtered = filtered.filter((r) => r.subject === filterSubject);
    if (filterBaseGrade) filtered = filtered.filter((r) => r.grade && r.grade.startsWith(filterBaseGrade));
    if (filterSection) filtered = filtered.filter((r) => r.grade && r.grade.endsWith(filterSection));
    filtered = [...filtered].sort((a, b) =>
        sortDir === "desc" ? b.score_percent - a.score_percent : a.score_percent - b.score_percent
    );

    const aprobados = filtered.filter((r) => r.score_percent >= 70).length;
    const intermedios = filtered.filter((r) => r.score_percent >= 50 && r.score_percent < 70).length;
    const desaprobados = filtered.filter((r) => r.score_percent < 50).length;
    const total = filtered.length || 1;
    const subjects = [...new Set(results.map((r) => r.subject).filter(Boolean))];
    const availableSections = filterBaseGrade === "5to Secundaria" ? SECTIONS : ["A", "B"];

    return (
        <div className="p-6 md:p-10 fade-in">
            <h1 className="text-3xl font-semibold mb-1">Calificaciones de los alumnos</h1>
            <p className="text-edu-body mb-6">Resumen de exámenes y trabajos calificados de los estudiantes.</p>

            <div className="flex flex-wrap gap-3 mb-6">
                <select className="edu-input" value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
                    <option value="">Todas las materias</option>
                    {subjects.map((s) => <option key={s}>{s}</option>)}
                </select>
                <select className="edu-input" value={filterBaseGrade} onChange={(e) => { setFilterBaseGrade(e.target.value); setFilterSection(""); }}>
                    <option value="">Todos los grados</option>
                    {BASE_GRADES.map((g) => <option key={g}>{g}</option>)}
                </select>
                <select className="edu-input" value={filterSection} onChange={(e) => setFilterSection(e.target.value)}>
                    <option value="">Todas las secciones</option>
                    {(filterBaseGrade ? availableSections : SECTIONS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="edu-input" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                    <option value="desc">Nota: mayor a menor</option>
                    <option value="asc">Nota: menor a mayor</option>
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <PieCard label="Aprobados" count={aprobados} total={total} color="bg-emerald-500" />
                <PieCard label="Intermedios" count={intermedios} total={total} color="bg-yellow-500" />
                <PieCard label="Desaprobados" count={desaprobados} total={total} color="bg-red-500" />
            </div>

            <div className="edu-card p-6">
                <h3 className="text-lg font-semibold mb-4">Resultados</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm hidden md:table">
                        <thead>
                            <tr className="text-left border-b border-edu">
                                <th className="py-2">Alumno</th>
                                <th className="py-2">Examen/Trabajo</th>
                                <th className="py-2">Grado / Sección</th>
                                <th className="py-2">Nota</th>
                                <th className="py-2">Fecha</th>
                                <th className="py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={6} className="py-6 text-center text-edu-muted">Sin resultados.</td></tr>
                            )}
                            {filtered.map((r, i) => {
                                const exam = r.kind === "examen" ? exams.find((e) => e.id === r.exam_id) : null;
                                return (
                                    <tr key={i} className="border-b border-edu/50">
                                        <td className="py-3">{r.user_name}</td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                {r.kind === "trabajo" ? (
                                                    <span className="edu-pill bg-blue-100 text-blue-700 inline-flex items-center gap-1 text-[10px]">
                                                        <FileText size={10} /> Trabajo
                                                    </span>
                                                ) : (
                                                    <span className="edu-pill bg-purple-100 text-purple-700 inline-flex items-center gap-1 text-[10px]">
                                                        <ClipboardCheck size={10} /> Examen
                                                    </span>
                                                )}
                                                <span>{r.exam_title}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 text-edu-muted">{r.grade || "—"}</td>
                                        <td className="py-3 font-semibold">{r.score_percent}%</td>
                                        <td className="py-3 text-edu-muted">{(r.submitted_at || "").slice(0, 10)}</td>
                                        <td className="py-3 text-right">
                                            {r.kind === "examen" ? (
                                                <button onClick={() => setViewing({ result: r, exam })} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                                    <Eye size={12} /> Ver Respuestas
                                                </button>
                                            ) : (
                                                <span className="text-xs text-edu-muted">
                                                    {r.score}/{r.max_score} pts
                                                    {r.feedback ? " · con comentario" : ""}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="md:hidden space-y-3">
                    {filtered.length === 0 && <p className="text-center py-6 text-edu-muted">Sin resultados.</p>}
                    {filtered.map((r, i) => {
                        const exam = r.kind === "examen" ? exams.find((e) => e.id === r.exam_id) : null;
                        const scoreColor = r.score_percent >= 70 ? "text-emerald-700" : r.score_percent >= 50 ? "text-yellow-700" : "text-red-700";
                        return (
                            <div key={i} className="border border-edu rounded-xl p-4">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold truncate">{r.user_name}</div>
                                        <div className="text-xs text-edu-muted truncate flex items-center gap-1">
                                            {r.kind === "trabajo" ? (
                                                <span className="edu-pill bg-blue-100 text-blue-700 inline-flex items-center gap-1 text-[9px]"><FileText size={9} /> Trabajo</span>
                                            ) : (
                                                <span className="edu-pill bg-purple-100 text-purple-700 inline-flex items-center gap-1 text-[9px]"><ClipboardCheck size={9} /> Examen</span>
                                            )}
                                            <span>{r.exam_title}</span>
                                        </div>
                                    </div>
                                    <div className={`text-2xl font-bold flex-shrink-0 ${scoreColor}`}>{r.score_percent}%</div>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2 text-xs text-edu-muted">
                                    {r.grade && <span className="edu-pill bg-gray-100">{r.grade}</span>}
                                    <span>{(r.submitted_at || "").slice(0, 10)}</span>
                                </div>
                                {r.kind === "examen" ? (
                                    <div className="flex gap-2 mt-3">
                                        <button onClick={() => setViewing({ result: r, exam })} className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-edu inline-flex items-center justify-center gap-1">
                                            <Eye size={12} /> Ver
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-xs text-edu-muted mt-3">
                                        {r.score}/{r.max_score} pts{r.feedback ? " · con comentario" : ""}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {viewing && <AnswersModal data={viewing} onClose={() => setViewing(null)} />}
        </div>
    );
}

function PieCard({ label, count, total, color }) {
    const pct = Math.round((count / total) * 100) || 0;
    return (
        <div className="edu-card p-6">
            <div className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2">{label}</div>
            <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold">{count}</span>
                <span className="text-sm text-edu-muted">({pct}%)</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full mt-3 overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function AnswersModal({ data, onClose }) {
    const { result, exam } = data;
    return createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto edu-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{result.user_name} · {result.score_percent}%</h3>
                    <button onClick={onClose} className="text-edu-muted hover:text-edu-navy"><X size={18} /></button>
                </div>
                {exam?.questions?.map((q, i) => {
                    const ans = result.answers[i];
                    const ok = ans === q.correct_index;
                    return (
                        <div key={i} className="border-b border-edu/50 py-3">
                            <p className="font-medium text-sm mb-1">{i + 1}. {q.question}</p>
                            <p className={`text-sm ${ok ? "text-emerald-700" : "text-red-700"}`}>Respuesta: {q.options[ans] ?? "—"}</p>
                            <p className="text-sm text-edu-muted">Correcta: {q.options[q.correct_index]}</p>
                        </div>
                    );
                })}
            </div>
        </div>,
        document.body
    );
}
